(function () {
  'use strict';

  const EVENT_NAME = 'ocs:pdp_interaction';
  const VARIANT_DATASET_KEYS = [
    'productId',
    'productHandle',
    'variantId',
    'itemId',
    'brand',
    'category',
    'category2',
    'category3',
    'ontarioGrown',
    'craft',
    'priceDrop',
    'new',
    'itemAvailability',
    'mediaCount',
  ];
  let pendingVariantSelection = null;

  function currentRoot() {
    return document.querySelector('[data-ocs-pdp-root]');
  }

  // Initialize PDP view exactly once on load, before interaction tracking
  const pdpRoot = currentRoot();
  if (pdpRoot && typeof window !== 'undefined' && window.OCSAnalyticsEvents?.initializePdpView) {
    // Pass exact normalized dataset fields from Liquid template
    const normalizedSelectedItem = {
      itemId: pdpRoot.dataset.itemId || '',
      itemName: pdpRoot.dataset.itemName || '',
      itemVariant: pdpRoot.dataset.itemVariant || '',
      brand: pdpRoot.dataset.brand || '',
      category: pdpRoot.dataset.category || '',
      category2: pdpRoot.dataset.category2 || '',
      category3: pdpRoot.dataset.category3 || '',
      priceCents: pdpRoot.dataset.priceCents || '',
      ontarioGrown: pdpRoot.dataset.ontarioGrown || '',
      craft: pdpRoot.dataset.craft || '',
      priceDrop: pdpRoot.dataset.priceDrop || '',
      new: pdpRoot.dataset.new || '',
      itemAvailability: pdpRoot.dataset.itemAvailability || '',
    };

    try {
      window.OCSAnalyticsEvents.initializePdpView({
        pdpRoot: pdpRoot,
        normalizedSelectedItem: normalizedSelectedItem,
      });
    } catch (error) {
      // Fail closed: view initialization errors do not block interaction tracking
    }
  }

  if (!pdpRoot) return;

  function publishPdpInteraction(details, root) {
    if (!root || typeof window.OCSAnalyticsEvents?.publishOcsAnalyticsEvent !== 'function') {
      return false;
    }

    try {
      return window.OCSAnalyticsEvents.publishOcsAnalyticsEvent(EVENT_NAME, buildPayload(details, root));
    } catch (error) {
      return false;
    }
  }

  function currentVariantId(root) {
    if (root.dataset.variantId) return root.dataset.variantId;

    const variantInput = root.querySelector('input[name="id"]');
    if (variantInput?.value) return variantInput.value;

    const variantButton = root.querySelector('[data-variant-id]');
    return variantButton?.dataset.variantId || '';
  }

  function currentQuantity(root) {
    const quantityControl = root.querySelector('select[name="quantity"], input[name="quantity"], quantity-selector-component input');
    return quantityControl?.value || '';
  }

  function textFor(element) {
    return (element?.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function compact(payload) {
    return Object.fromEntries(
      Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== ''),
    );
  }

  function productPayload(root) {
    return compact({
      event_source: 'pdp',
      page_type: 'product',
      product_id: root.dataset.productId,
      product_handle: root.dataset.productHandle,
      variant_id: currentVariantId(root),
      quantity: currentQuantity(root),
      item_id: root.dataset.itemId,
      brand: root.dataset.brand,
      category: root.dataset.category,
      category2: root.dataset.category2,
      category3: root.dataset.category3,
      ontario_grown: root.dataset.ontarioGrown,
      craft: root.dataset.craft,
      price_drop: root.dataset.priceDrop,
      new: root.dataset.new,
      item_availability: root.dataset.itemAvailability,
      locale: document.documentElement.lang || window.Shopify?.locale,
    });
  }

  // The acceptance schema is component_name + input_value. Only these two carry
  // the interaction semantics; the product metadata base is retained for GA4.
  /**
   * @param {string} componentName
   * @param {string} inputValue
   */
  function interaction(componentName, inputValue) {
    return { component_name: componentName, input_value: inputValue };
  }

  function buildPayload(details, root) {
    return compact({
      ...productPayload(root),
      ...details,
    });
  }

  // Resolve which carousel a slideshow control belongs to. The main media
  // gallery, More Items to Consider, and Recently Viewed all use the shared
  // Horizon slideshow markup, so the enclosing component identifies the module.
  /** @param {Element} element */
  function carouselComponentName(element) {
    if (element.closest('ocs-suggested-accessories')) return 'More Items to Consider';
    if (element.closest('ocs-recently-viewed')) return 'Recently Viewed';
    if (element.closest('.media-gallery, media-gallery')) return 'Product Gallery';
    return '';
  }

  // Map a CTA section to its fixed English component_name by matching the
  // section heading (EN or FR copy) that the merchant configured.
  /** @param {Element} element */
  function ctaComponentName(element) {
    const section = element.closest('.shopify-section, .section');
    if (!section) return '';
    const heading = section.querySelector ? section.querySelector('h1, h2, h3, h4') : null;
    const headingText = textFor(heading);
    if (!headingText) return '';
    if (/how to choose|choisir des produits/i.test(headingText)) return 'How To Choose Cannabis Products';
    if (/looking for something different|cherchez-vous d['\u2019]autres/i.test(headingText)) return 'Looking for Something Different?';
    return '';
  }

  // Best-available tooltip subject label (e.g. THC, CBD, plant type).
  /** @param {Element} trigger */
  function tooltipLabel(trigger) {
    return (
      trigger.getAttribute('data-tooltip-label') ||
      trigger.getAttribute('aria-label') ||
      trigger.getAttribute('title') ||
      textFor(trigger)
    );
  }

  document.addEventListener('click', (event) => {
    if (!event.isTrusted) return;

    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    const root = currentRoot();
    if (!target || !root) return;

    // Breadcrumbs (sibling section, outside the PDP root).
    const breadcrumbLink = target.closest('.breadcrumbs a[href]');
    if (breadcrumbLink) {
      publishPdpInteraction(interaction('breadcrumb', textFor(breadcrumbLink)), root);
      return;
    }

    // Slideshow arrow/dot controls across the media gallery and the two module
    // carousels (More Items to Consider, Recently Viewed).
    const slideshowArrow = target.closest('.slideshow-control');
    if (slideshowArrow) {
      const componentName = carouselComponentName(slideshowArrow);
      if (componentName) {
        publishPdpInteraction(interaction(componentName, 'Arrow navigation'), root);
        return;
      }
    }

    const slideshowDot = target.closest('.slideshow-controls__dots button');
    if (slideshowDot) {
      const componentName = carouselComponentName(slideshowDot);
      if (componentName) {
        publishPdpInteraction(interaction(componentName, 'Pagination'), root);
        return;
      }
    }

    // Product clicks inside the module carousels carry the product title as the
    // input_value (e.g. More Items to Consider / "Soft Chews").
    const suggestedProductLink = target.closest('ocs-suggested-accessories a[href*="/products/"]');
    if (suggestedProductLink) {
      publishPdpInteraction(interaction('More Items to Consider', textFor(suggestedProductLink)), root);
      return;
    }

    const recentlyViewedLink = target.closest('ocs-recently-viewed a[href*="/products/"]');
    if (recentlyViewedLink) {
      publishPdpInteraction(interaction('Recently Viewed', textFor(recentlyViewedLink)), root);
      return;
    }

    // CTA modules (sibling sections identified by heading copy).
    const ctaControl = target.closest('a[href], button');
    if (ctaControl) {
      const ctaName = ctaComponentName(ctaControl);
      if (ctaName) {
        publishPdpInteraction(interaction(ctaName, textFor(ctaControl)), root);
        return;
      }
    }

    const scopedTarget = root.contains(target) ? target : null;
    if (!scopedTarget) return;

    // Brand name click.
    const brandLink = scopedTarget.closest('.ocs-vendor__link');
    if (brandLink) {
      publishPdpInteraction(interaction('brand_click', textFor(brandLink)), root);
      return;
    }

    // Tooltip open/close interactions.
    const tooltipTrigger = scopedTarget.closest('.ocs-tooltip-trigger');
    if (tooltipTrigger) {
      publishPdpInteraction(interaction('tooltip', tooltipLabel(tooltipTrigger)), root);
      return;
    }

    // Legacy fallback for main-gallery arrow buttons that lack the standard
    // .slideshow-control class. Dots are handled by the generic
    // .slideshow-controls__dots branch above; this fallback is arrow-only so a
    // dot is never mislabeled as Arrow navigation.
    const galleryArrow = scopedTarget.closest('.media-gallery slideshow-arrows button');
    if (galleryArrow) {
      publishPdpInteraction(interaction('Product Gallery', 'Arrow navigation'), root);
      return;
    }
  }, true);

  document.addEventListener('change', (event) => {
    if (!event.isTrusted) return;

    const target = event.target instanceof Element ? event.target : null;
    const root = currentRoot();
    if (!target || !root?.contains(target)) return;

    // Variant selection is tracked only to keep the selected-variant metadata
    // synchronized (VIEW/DATA coherence). It does not publish a pdp_interaction
    // (PDP-004: out-of-scope interaction removed).
    if (target.matches('variant-picker input, variant-picker select')) {
      const selectedOption = target.matches('select') ? target.options?.[target.selectedIndex] : target;

      if (selectedOption?.dataset.connectedProductUrl) {
        pendingVariantSelection = null;
        return;
      }

      pendingVariantSelection = {
        optionValue: target.value,
        picker: target.closest('variant-picker'),
      };
    }
  }, true);

  document.addEventListener('variant:update', (event) => {
    const root = currentRoot();
    if (!root || !pendingVariantSelection) return;
    if (event.target !== pendingVariantSelection.picker || !root.contains(event.target)) return;

    const refreshedRoot = event.detail?.data?.html?.querySelector?.('[data-ocs-pdp-root]');
    pendingVariantSelection = null;

    if (!refreshedRoot) return;

    // Synchronize the selected-variant metadata so later interactions and the
    // view/add contracts read coherent values. No pdp_interaction is published
    // for the variant selection itself (PDP-004).
    for (const key of VARIANT_DATASET_KEYS) {
      root.dataset[key] = refreshedRoot.dataset[key] ?? '';
    }
  });

  document.addEventListener('toggle', (event) => {
    if (!event.isTrusted) return;

    const details = event.target instanceof HTMLDetailsElement ? event.target : null;
    const root = currentRoot();
    if (!details || !root || !details.closest('.ocs-product-accordions')) return;

    const summary = details.querySelector('summary');
    publishPdpInteraction(interaction('accordion', textFor(summary)), root);
  }, true);
})();
